import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchProducts } from "../lib/api";
import Overline from "../components/Overline";
import Reveal from "../components/Reveal";
import PatternStrip from "../components/PatternStrip";

const WeightToggle = ({ value, onChange, prices }) => (
  <div className="inline-flex border border-elle text-[11px] tracking-overline uppercase" data-testid="weight-toggle">
    {["500g", "1kg"].map((w) => (
      <button
        key={w}
        onClick={() => onChange(w)}
        data-testid={`weight-btn-${w}`}
        className={`px-4 py-2 transition-colors duration-300 ${
          value === w ? "bg-elle-green text-elle-cream" : "text-elle-green hover:bg-elle-muted"
        }`}
      >
        {w} · ${prices[w]}
      </button>
    ))}
  </div>
);

const ProductCard = ({ product }) => {
  const [weight, setWeight] = useState("500g");
  const prices = {
    "500g": product.price_500g.toFixed(0),
    "1kg": product.price_1kg.toFixed(0),
  };
  const buildUrl = (base) =>
    `${base}${base.includes("?") ? "&" : "?"}variant=${weight}`;
  return (
    <Reveal className="group">
      <div className="relative aspect-[4/5] overflow-hidden bg-elle-sand">
        <Link to={`/product/${product.slug}`} data-testid={`shop-product-image-${product.slug}`}>
          <img
            src={product.image_front}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
          />
        </Link>
      </div>
      <div className="mt-6">
        <Overline className="!text-elle-green/60">{product.subtitle}</Overline>
        <div className="flex items-start justify-between gap-4 mt-2">
          <Link
            to={`/product/${product.slug}`}
            data-testid={`shop-product-title-${product.slug}`}
            className="font-serif-display text-3xl md:text-4xl text-elle-green link-underline"
          >
            {product.name.replace("Ell'e Coffee — ", "")}
          </Link>
          <div className="text-[13px] text-elle-soft whitespace-nowrap mt-2">
            {product.altitude}
          </div>
        </div>
        <p className="mt-3 text-[14px] text-elle-soft leading-relaxed max-w-md">
          {product.description}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {product.tasting_notes.slice(0, 4).map((n) => (
            <span
              key={n}
              className="text-[11px] tracking-overline uppercase border border-elle px-3 py-1 text-elle-green"
            >
              {n}
            </span>
          ))}
        </div>

        <div className="mt-8 space-y-5">
          <WeightToggle value={weight} onChange={setWeight} prices={prices} />

          <div className="flex flex-wrap gap-3">
            <a
              href={buildUrl(product.shopify_url)}
              target="_blank"
              rel="noreferrer"
              data-testid={`shop-shopify-btn-${product.slug}`}
              className="inline-flex items-center gap-2 bg-elle-green text-elle-cream px-6 py-3.5 text-[11px] tracking-overline uppercase hover:bg-[var(--elle-green-deep)] transition-colors duration-500"
            >
              Buy on Shopify <ArrowUpRight size={14} />
            </a>
            <a
              href={buildUrl(product.amazon_url)}
              target="_blank"
              rel="noreferrer"
              data-testid={`shop-amazon-btn-${product.slug}`}
              className="inline-flex items-center gap-2 bg-elle-red text-elle-cream px-6 py-3.5 text-[11px] tracking-overline uppercase hover:bg-[var(--elle-red-deep)] transition-colors duration-500"
            >
              Buy on Amazon <ArrowUpRight size={14} />
            </a>
            <Link
              to={`/product/${product.slug}`}
              data-testid={`shop-details-btn-${product.slug}`}
              className="link-underline self-center text-[11px] tracking-overline uppercase text-elle-green ml-1"
            >
              Tasting Notes →
            </Link>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then((d) => { setProducts(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="page-shop">
      {/* Header */}
      <section className="pt-12 md:pt-20 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Overline>Shop</Overline>
          <h1 className="font-serif-display text-6xl md:text-8xl text-elle-green mt-6 leading-[0.95]">
            Two coffees.<br/>
            <span className="italic">Two memories.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[16px] text-elle-soft leading-relaxed">
            Each Ell'e expression is single-origin, 100% Arabica, grown between
            1,800 – 2,200 metres in the Sidama region of Ethiopia. Choose your
            weight and checkout directly through Shopify or Amazon.
          </p>
        </div>
      </section>

      <PatternStrip height={8} />

      {/* Grid */}
      <section className="py-20 md:py-28 bg-elle-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20 text-elle-soft" data-testid="shop-loading">Loading the harvest…</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-12 md:gap-20">
              {products.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
